/* istanbul ignore file - tested e2e */

import { LoadingSpinner } from '@components/Loading';
import { cadExportInstructions } from '@constants/URLs';

import {
  faCircleX,
  faCloudExclamation,
  faFileCad,
  faRight,
  faWandMagicSparkles,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCancelCadJob } from '@pages/CreateNewProductPage/ProcessingPage/useCancelCadJob';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import type { CADJobStatusOptions } from '@partful/shared-types';
import { Button } from '@partful/shared/ui/Button';
import {
  WizardPageContent,
  WizardPageFooter,
  WizardPageHeader,
  WizardPageLayout,
} from '@partful/shared/ui/layouts/WizardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { getCADJob, patchCADJob } from '@services/productInventory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Redirect, useLocation } from 'react-router-dom';
import styles from './ProcessingPage.module.scss';

export function ProcessingPage() {
  const { t: i18n } = useTranslation();
  const { accountId } = useTenant();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const runID = searchParams.get('runID');
  const productName = searchParams.get('productName');

  const {
    isError,
    isLoading,
    data: runInfo,
  } = useQuery(
    ['getCadRunInfo', runID],
    () => {
      if (runID) {
        return getCADJob(runID, accountId);
      }
    },
    {
      refetchInterval: 10000,
      enabled: Boolean(runID),
    }
  );

  const {
    isLoading: isPatchCadJobLoading,
    isError: isPatchCadJobError,
    mutate: patchCadJobMutation,
  } = useMutation(['patchCADJobMutation', runID], ({ runID, status }: { runID: string; status: CADJobStatusOptions }) =>
    patchCADJob(runID, accountId, { status })
  );

  useEffect(() => {
    if (runID && runInfo?.status === 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING') {
      patchCadJobMutation({ runID, status: 'IN_PROGRESS_REQUIRES_ORIENTATION_MAPPING' });
    }
  }, [patchCadJobMutation, runID, runInfo?.status]);

  const { cancelJob } = useCancelCadJob();
  const handleCancel = useCallback(() => {
    if (runID) {
      cancelJob(runID, accountId);
    }
  }, [accountId, cancelJob, runID]);

  if (isLoading || isPatchCadJobLoading) {
    return <LoadingPage />;
  }

  if (isError || isPatchCadJobError) {
    return (
      <WizardErrorPage
        pageTitle={
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
          </>
        }
        pageSubtitle={i18n('instructions:create.processing.title')}
        errorTitle={i18n('instructions:create.processing.loadFailedError.title')}
        errorMessage={i18n('instructions:create.processing.loadFailedError.message')}
        errorIcon={<FontAwesomeIcon icon={faCloudExclamation} />}
        errorActions={
          <Button linkComponent={Link} to="/instructions">
            <span>
              {i18n('instructions:create.processing.loadFailedError.backToLibrary')} <FontAwesomeIcon icon={faRight} />
            </span>
          </Button>
        }
      />
    );
  }

  if (runInfo?.status === 'CANCELLED_BY_USER') {
    return <Redirect to="/instructions/create" />;
  }

  // if the axis orientation and handedness haven't been set, redirect to preview page

  if (runInfo?.status === 'IN_PROGRESS_REQUIRES_ORIENTATION_MAPPING') {
    return (
      <Redirect
        to={`/instructions/create/preview?runID=${runID}&configurationPID=${runInfo?.configurationPID}&productName=${productName}`}
      />
    );
  }

  if (runInfo?.status === 'IN_PROGRESS_CAD_PROCESSING_FAILED') {
    return (
      <WizardErrorPage
        pageTitle={
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
          </>
        }
        pageSubtitle={i18n('instructions:create.processing.title')}
        errorTitle={i18n('instructions:create.processing.error.title', { productName })}
        errorMessage={
          <>
            <ul className={styles.errorReferences}>
              <Typography as="li">
                {i18n('instructions:create.processing.error.reference', { reference: runID })}
              </Typography>
            </ul>
            <Typography as="p">
              <Trans i18nKey="instructions:create.processing.error.message">
                <Typography as="a" href={cadExportInstructions} target="_blank" rel="noreferrer" />
              </Trans>
            </Typography>
          </>
        }
        errorIcon={<FontAwesomeIcon icon={faFileCad} />}
        errorActions={
          <Button linkComponent={Link} to={`/instructions/create/details?runID=${runID}`}>
            <span>
              {i18n('instructions:create.processing.error.uploadNewFile')} <FontAwesomeIcon icon={faRight} />
            </span>
          </Button>
        }
      />
    );
  }

  return (
    <WizardPageLayout>
      <WizardPageHeader>
        <Typography appearance="heading5" as="h1">
          <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
        </Typography>
        <Typography appearance="heading6" as="h2" data-testid="processing-title">
          {i18n('instructions:create.processing.title')}
        </Typography>
      </WizardPageHeader>
      <WizardPageContent className={styles.processing}>
        <div className={styles.message} role="alert">
          <div className={styles.icon}>
            <LoadingSpinner viewBox="38 38 24 24" width="50" height="50" />
          </div>
          <div className={styles.body}>
            <Typography appearance="heading6" as="h3">
              {i18n('instructions:create.processing.inProgress.title', { productName })}
            </Typography>
            <Typography as="p">{i18n('instructions:create.processing.inProgress.message')}</Typography>
            <div className={styles.actions}>
              <Button variant="secondary" size="small" onClick={handleCancel}>
                {i18n('instructions:create.processing.inProgress.cancelAndRestart')}
                <FontAwesomeIcon icon={faCircleX} />
              </Button>
            </div>
          </div>
        </div>
      </WizardPageContent>
      <WizardPageFooter />
    </WizardPageLayout>
  );
}
